import React, { useEffect, useState } from 'react';
import { AiOutlineCopy } from 'react-icons/ai';
import { BiSearch } from 'react-icons/bi';
import { BsFilePdf, BsWhatsapp } from 'react-icons/bs';
import { HiOutlineMail } from 'react-icons/hi';
import { VscClose } from 'react-icons/vsc';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Green3, PrimaryTitleGray } from '../../../app/theme/tokens/colors';
import {
  InputText,
  LighterGreenButton,
  PrimaryButton,
  SecondaryButton,
} from '../../../shared/ui/Inputs';
import { Spinner } from '../../../shared/ui/Spinners';
import { OrderFlowTable } from '../../../shared/ui/orderFlowTable';
import { LargeTile } from '../../../shared/ui/grid/gridsystem';
import { WideLayout } from '../../../shared/layout/widelayout';
import { Panel } from '../../../shared/ui/panel/panels';
import { SimplePopup } from '../../../shared/overlays';
import {
  getEquipmentsByCPF,
  searchAssistancePerEquipment,
} from '../../../services/technical-assistance/assistanceService';
import { saveDocument } from '../../../services/documents/documentService';
import { getProductsGuides } from '../../../services/products/productService';
import { usePortalStore } from '../../../stores/portalStore';
import { useTechnicalAssistanceStore } from '../store/technicalAssistanceStore';
import { downloadPDF, getPdfDownloadUrl } from '../../../utils/documents';
import { genericOrderCode } from '../../../utils/orders';
import { getLastFromSplit } from '../../../utils/text';
import { ArrowForward } from '@mui/icons-material';
import { Tooltip } from '../../../shared/ui/Tooltip';
import { useResponsive } from '../../../hooks/useResponsive';
import { OrderFlowPageHeader } from '../../../shared/ui/orderFlowPageHeader';

// ── Toast ──────────────────────────────────────────────────────────────────
const Toast = ({ message, onClose }) => (
  <div
    style={{
      position: 'fixed',
      bottom: '24px',
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: 99999,
      backgroundColor: '#1a1a1a',
      color: '#fff',
      padding: '12px 20px',
      borderRadius: '8px',
      fontSize: '13px',
      boxShadow: '0 4px 16px rgba(0,0,0,0.22)',
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      maxWidth: '480px',
      width: '90%',
    }}
  >
    <span style={{ flex: 1 }}>{message}</span>
    <VscClose
      onClick={onClose}
      style={{ cursor: 'pointer', fontSize: '18px', flexShrink: 0 }}
    />
  </div>
);

// ── Estado vazio ───────────────────────────────────────────────────────────
const EmptyState = ({ message }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '32px 20px',
      gap: '10px',
      color: PrimaryTitleGray,
    }}
  >
    <svg
      width="40"
      height="40"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="8" x2="12" y2="12" />
      <line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
    <p
      style={{
        margin: 0,
        fontSize: '13px',
        textAlign: 'center',
        lineHeight: '1.6',
      }}
    >
      {message}
    </p>
  </div>
);

const MainPanels = () => {
  const { isMobile, isTablet } = useResponsive();
  const isTabletOrMobile = isMobile || isTablet;
  const currentUser = usePortalStore((state) => state.currentUser);
  const selectedPatient = useTechnicalAssistanceStore(
    (state) => state.selectedPatient
  );
  const setTypedSearch = useTechnicalAssistanceStore(
    (state) => state.setTypedSearch
  );
  const typedSearch = useTechnicalAssistanceStore((state) => state.typedSearch);
  const selectedEquipments = useTechnicalAssistanceStore(
    (state) => state.selectedEquipments
  );
  const setSelectedEquipment = useTechnicalAssistanceStore(
    (state) => state.setSelectedEquipment
  );
  const equipmentList = useTechnicalAssistanceStore(
    (state) => state.equipmentList
  );
  const setEquipmentList = useTechnicalAssistanceStore(
    (state) => state.setEquipmentList
  );
  const selectedSearch = useTechnicalAssistanceStore(
    (state) => state.selectedSearch
  );
  const setSelectedSearch = useTechnicalAssistanceStore(
    (state) => state.setSelectedSearch
  );

  const [search, setSearch] = useState([]);
  const [guides, setGuides] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [showCreateLinkPopup, setCreateLinkPopup] = useState(false);
  const [isGeneratingLink, setGeneratingLink] = useState('');
  const [currentSelectedEquipment, setCurrentSelectedEquipment] = useState(-1);
  const [searchParams] = useSearchParams();
  const [selectedGuides, setSelectedGuides] = useState({});
  const [selectedSearchFinish, setSelectedSearchFinish] = useState({});
  const [toastMessage, setToastMessage] = useState('');
  const navigate = useNavigate();
  const popupHeight = window.innerHeight;

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(''), 4000);
  };

  useEffect(() => {
    if (!selectedPatient?.cpf) {
      navigate('/technicalAssistance/assistanceToPatient');
    }
  }, [selectedPatient, navigate]);

  useEffect(() => {
    if (!selectedPatient?.cpf) return;

    (async () => {
      const keepState = searchParams.get('keepState');
      if (keepState !== 'true') {
        setIsLoading(true);
        setSelectedSearch({});
        setTypedSearch({});
        let equipments = await getEquipmentsByCPF(
          selectedPatient.cnpj,
          selectedPatient.cpf
        );
        equipments = equipments.filter((eq) => eq.subCategory !== 'Cilindros');
        setSelectedEquipment({}, '');
        setEquipmentList([...equipments]);
        const g = await getProductsGuides(equipments.map((x) => x.id));
        setGuides([...g]);
        setIsLoading(false);
      }
    })();
  }, [selectedPatient?.cpf, selectedPatient?.cnpj, searchParams]);

  if (!selectedPatient?.cpf) {
    return (
      <div
        style={{
          width: '100%',
          minHeight: '60vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Spinner />
      </div>
    );
  }

  const getEquipmentQuantity = (equipment) => {
    const rawQuantity =
      equipment?.total_balance ?? equipment?.totalBalance ?? equipment?.qty;
    const parsedQuantity = Number(rawQuantity);
    return Number.isFinite(parsedQuantity) ? parsedQuantity : 0;
  };

  // ── Selecionar todos ───────────────────────────────────────────────────
  const handleSelectAll = (checked) => {
    const activeEquipments = equipmentList.filter(
      (eq) => getEquipmentQuantity(eq) > 0
    );
    if (checked) {
      const newSelected = {};
      activeEquipments.forEach((eq) => {
        newSelected[eq.id] = true;
      });
      setSelectedEquipment({ ...newSelected }, typedSearch);
    } else {
      setSelectedEquipment({}, '');
    }
  };

  const selectEquipment = (id, checked) => {
    const equipment = equipmentList.find((eq) => eq.id == id);
    if (getEquipmentQuantity(equipment) <= 0) return;
    setSelectedEquipment({ ...selectedEquipments, [id]: checked }, typedSearch);
    if (checked) {
      setCurrentSelectedEquipment(id);
      setShowPopup(true);
      autoSearch(id);
    } else {
      delete selectedSearch[id];
      setSelectedSearch(selectedSearch);
    }
  };

  // ── Prosseguir com validação ───────────────────────────────────────────
  const proceed = () => {
    const selectedIds = Object.keys(selectedEquipments).filter(
      (id) => selectedEquipments[id] === true
    );

    const pendingEquipments = selectedIds.filter(
      (id) => !selectedSearch[id] || selectedSearch[id].length === 0
    );

    if (pendingEquipments.length > 0) {
      const names = pendingEquipments
        .map(
          (id) => equipmentList.find((eq) => eq.id == id)?.name ?? `ID ${id}`
        )
        .join(', ');
      showToast(
        `Sem solução selecionada para: ${names}. Você poderá prosseguir, mas deverá descrever o problema para concluir.`
      );
    }

    const normalizedSelectedSearch = { ...selectedSearch };

    Object.keys(normalizedSelectedSearch).forEach((eqId) => {
      const selectedItems = Array.isArray(normalizedSelectedSearch[eqId])
        ? normalizedSelectedSearch[eqId]
        : [];

      const finishMap = selectedSearchFinish?.[eqId];
      if (!finishMap) {
        normalizedSelectedSearch[eqId] = selectedItems;
        return;
      }

      normalizedSelectedSearch[eqId] = selectedItems.filter(
        (item) => finishMap?.[item?.genId] !== false
      );
    });

    setSelectedSearch(normalizedSelectedSearch);
    navigate('/technicalAssistance/finishAssistanceOrder');
  };

  const proceedWithGenericRequest = () => {
    setSelectedEquipment(
      { ...selectedEquipments, [genericOrderCode]: true },
      ''
    );
    navigate('/technicalAssistance/finishAssistanceOrder');
  };

  const checkSearch = (rowIdx, checked) => {
    adjustSelection(currentSelectedEquipment, rowIdx, checked);
  };

  const isSearchSelected = (equipmentId, rowIdx) => {
    const normalizedRowId = String(rowIdx);
    const finishMap = selectedSearchFinish?.[equipmentId];
    if (
      finishMap &&
      Object.prototype.hasOwnProperty.call(finishMap, normalizedRowId)
    ) {
      return Boolean(finishMap[normalizedRowId]);
    }

    const selectedItems = selectedSearch?.[equipmentId] ?? [];
    return selectedItems.some(
      (item) => String(item?.genId) === normalizedRowId
    );
  };

  const adjustSelection = (eqId, rowIdx, checked, mustRemove = true) => {
    const currId = String(eqId);
    const normalizedRowId = String(rowIdx);
    const currentSelected = Array.isArray(selectedSearch?.[currId])
      ? [...selectedSearch[currId]]
      : [];

    const finishMap = {
      ...(selectedSearchFinish?.[currId] ?? {}),
      [normalizedRowId]: checked,
    };

    let nextSelected = currentSelected;

    if (checked) {
      const toAdd = search.find(
        (item) => String(item?.genId) === normalizedRowId
      );
      const alreadySelected = currentSelected.some(
        (item) => String(item?.genId) === normalizedRowId
      );

      if (toAdd && !alreadySelected) {
        nextSelected = [...currentSelected, toAdd];
      }
    } else if (mustRemove) {
      nextSelected = currentSelected.filter(
        (item) => String(item?.genId) !== normalizedRowId
      );
    }

    setSelectedSearch({
      ...selectedSearch,
      [currId]: nextSelected,
    });

    setSelectedSearchFinish({
      ...selectedSearchFinish,
      [currId]: finishMap,
    });
  };

  const tableResult = () => {
    if (!search || search.length === 0) return null;
    return (
      <div>
        <h3
          style={{
            fontSize: '14px',
            fontWeight: '600',
            color: PrimaryTitleGray,
            margin: '0 0 10px',
          }}
        >
          Resultado da pesquisa
        </h3>
        <div
          className="table-assistencia2"
          style={{ width: '100%', overflowX: 'auto', overflowY: 'hidden' }}
        >
          <div style={{ minWidth: isTabletOrMobile ? '640px' : '100%' }}>
            <OrderFlowTable
              ariaLabel="Resultados da pesquisa de assistência"
              header={['', 'Problemas', 'Causas', 'Como proceder']}
              data={search.map((s, idx) => [
                <input
                  key={idx}
                  type="checkbox"
                  value={s.genId}
                  checked={isSearchSelected(currentSelectedEquipment, s.genId)}
                  onChange={(e) =>
                    checkSearch(e.target.value, e.target.checked)
                  }
                />,
                s.issue,
                s.cause,
                s.howToProceed,
              ])}
            />
          </div>
        </div>
      </div>
    );
  };

  const autoSearch = (eqId) => {
    setSearchLoading(true);
    (async () => {
      const results = await searchAssistancePerEquipment(
        currentUser.cnpj,
        eqId,
        ''
      );
      setSearchLoading(false);
      setSearch(results);
    })();
  };

  const doSearch = (e, click) => {
    if (click === true || e.key === 'Enter' || e.keyCode === 13) {
      setSearchLoading(true);
      (async () => {
        const results = await searchAssistancePerEquipment(
          currentUser.cnpj,
          currentSelectedEquipment,
          searchTerm
        );
        setSearchLoading(false);
        if (!typedSearch[currentSelectedEquipment])
          typedSearch[currentSelectedEquipment] = [];
        typedSearch[currentSelectedEquipment].push(searchTerm);
        setTypedSearch({ ...typedSearch });
        setSearch(results);
      })();
    }
  };

  const closePopup = () => {
    setCurrentSelectedEquipment(-1);
    setShowPopup(false);
    setSearch([]);
    setGeneratingLink('');
  };

  const createLink = () => {
    const docList = [];
    Object.keys(selectedEquipments)
      .filter(
        (x) => selectedEquipments[x] === true && x === currentSelectedEquipment
      )
      .map((id) => equipmentList.filter((x) => x.id == id)[0])
      .forEach((equipment) => {
        docList.push({
          name: equipment.name,
          solutions: selectedSearch[equipment.id.toString()],
        });
      });
    saveDocToShareLink({
      files: Object.values(selectedGuides),
      search: docList,
    });
  };

  const saveDocToShareLink = async (doc) => {
    const key = await saveDocument(doc);
    setGeneratingLink(`${window.location.origin}/link?key=${key.key}`);
  };

  const toggleSelectedGuide = (file, idx) => {
    if (selectedGuides[idx]) {
      delete selectedGuides[idx];
    } else {
      selectedGuides[idx] = file;
    }
    setSelectedGuides({ ...selectedGuides });
  };

  // ── Popup: Compartilhamento ────────────────────────────────────────────
  const createLinkPopup = () => (
    <div style={{ width: '100%', position: 'relative' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '16px',
        }}
      >
        <h3
          style={{
            margin: 0,
            color: PrimaryTitleGray,
            fontSize: '15px',
            fontWeight: '600',
          }}
        >
          Compartilhamento{' '}
          <span style={{ fontWeight: 'normal', color: '#666' }}>
            — Selecione os arquivos que deseja enviar
          </span>
        </h3>
        <VscClose
          style={{
            fontSize: '20px',
            cursor: 'pointer',
            color: PrimaryTitleGray,
          }}
          onClick={() => setCreateLinkPopup(false)}
        />
      </div>

      {Object.keys(selectedEquipments)
        .filter(
          (x) =>
            selectedEquipments[x] === true && x === currentSelectedEquipment
        )
        .map((id) => equipmentList.filter((x) => x.id == id)[0])
        .map((equipment, idx) => (
          <div key={idx}>
            {guides
              .filter(
                (g) => parseInt(g.product_code) === parseInt(equipment.id)
              )
              .flat()
              .map((file) =>
                file.guides.map((guide, jjj) => (
                  <div
                    key={jjj}
                    onClick={() =>
                      toggleSelectedGuide(
                        { product_code: file.product_code, guide },
                        jjj
                      )
                    }
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px 12px',
                      marginBottom: '8px',
                      borderRadius: '6px',
                      border: `1px solid ${
                        selectedGuides[jjj] ? '#2f7c0066' : '#e8e8e8'
                      }`,
                      backgroundColor: selectedGuides[jjj]
                        ? '#2f7c0012'
                        : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.15s ease',
                    }}
                  >
                    <BsFilePdf
                      style={{
                        fontSize: '22px',
                        color: '#e53935',
                        flexShrink: 0,
                      }}
                    />
                    <span style={{ fontSize: '13px', color: PrimaryTitleGray }}>
                      {getLastFromSplit(guide, '_')}
                    </span>
                  </div>
                ))
              )}
          </div>
        ))}

      {isGeneratingLink !== '' ? (
        <>
          <p style={{ fontSize: '13px', color: '#555', margin: '16px 0 4px' }}>
            Você pode encaminhar este link por WhatsApp e e-mail.
          </p>
          <p style={{ fontSize: '11px', color: '#999', margin: '0 0 12px' }}>
            Este link expira após 24 horas.
          </p>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid #d4edda',
              backgroundColor: '#f3fdf6',
              color: '#255a33',
              gap: '8px',
            }}
          >
            <span style={{ fontSize: '12px', wordBreak: 'break-all', flex: 1 }}>
              {isGeneratingLink}
            </span>
            <AiOutlineCopy
              style={{ cursor: 'pointer', fontSize: '22px', flexShrink: 0 }}
              onClick={() => navigator.clipboard.writeText(isGeneratingLink)}
            />
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginTop: '12px',
            }}
          >
            <span style={{ fontSize: '13px', color: '#555' }}>
              Compartilhar via
            </span>
            <a
              href={`https://api.whatsapp.com/send?text=${isGeneratingLink}`}
              target="_blank"
              rel="noreferrer"
            >
              <BsWhatsapp style={{ fontSize: '26px', color: Green3 }} />
            </a>
            <a
              href={`mailto:${selectedPatient.email}?subject=Portal Homecare&body=${isGeneratingLink}`}
              target="_blank"
              rel="noreferrer"
            >
              <HiOutlineMail style={{ fontSize: '28px', color: Green3 }} />
            </a>
          </div>
        </>
      ) : (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            marginTop: '20px',
          }}
        >
          <PrimaryButton onClick={() => createLink()}>
            Gerar Link de Compartilhamento
          </PrimaryButton>
        </div>
      )}
    </div>
  );

  const openSharePopup = () => {
    setGeneratingLink('');
    setCreateLinkPopup(true);
  };

  // ── Popup: Guia de soluções ────────────────────────────────────────────
  const showHelp = () => {
    const equipmentName = (
      (Object.keys(selectedEquipments)
        .filter(
          (x) =>
            selectedEquipments[x] === true &&
            parseInt(x) === parseInt(currentSelectedEquipment)
        )
        .map(
          (id) =>
            equipmentList.filter((x) => parseInt(x.id) === parseInt(id))[0]
        ) ?? [])[0] ?? {}
    )?.name;

    const currentGuides = guides
      .filter(
        (g) => parseInt(g.product_code) === parseInt(currentSelectedEquipment)
      )
      .flatMap((file) => file.guides.map((guide) => ({ file, guide })));

    const hasSearchResults = search && search.length > 0;
    const currentSelectedRows = (selectedSearch[currentSelectedEquipment] ?? [])
      .filter((item) =>
        isSearchSelected(currentSelectedEquipment, String(item?.genId))
      )
      .filter(
        (item, index, array) =>
          array.findIndex((x) => String(x?.genId) === String(item?.genId)) ===
          index
      );

    return (
      <div
        style={{
          boxSizing: 'border-box',
          padding: isTabletOrMobile ? '12px' : '20px',
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          minHeight: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            marginBottom: '16px',
          }}
        >
          <div>
            <p
              style={{
                margin: '0 0 2px',
                fontSize: '11px',
                fontWeight: '600',
                color: '#999',
                letterSpacing: '0.5px',
                textTransform: 'uppercase',
              }}
            >
              Guia de Soluções
            </p>
            <h3
              style={{
                margin: 0,
                fontSize: '16px',
                fontWeight: '700',
                color: PrimaryTitleGray,
              }}
            >
              {equipmentName}
            </h3>
          </div>
          <VscClose
            onClick={closePopup}
            style={{
              fontSize: '22px',
              cursor: 'pointer',
              color: PrimaryTitleGray,
              marginTop: '2px',
            }}
          />
        </div>

        {/* Barra de pesquisa + ações */}
        <div
          style={{
            display: 'flex',
            flexDirection: isTabletOrMobile ? 'column' : 'row',
            gap: '10px',
            marginBottom: '16px',
          }}
        >
          <div style={{ display: 'flex', flex: 1 }}>
            <InputText
              onKeyUp={(e) => doSearch(e)}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ width: '100%' }}
              placeholder="Informe a dúvida que deseja assistência"
            />
            <LighterGreenButton
              onClick={(e) => doSearch(e, true)}
              style={{
                marginLeft: '6px',
                width: '40px',
                minWidth: '40px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <BiSearch style={{ fontSize: '18px', color: '#fff' }} />
            </LighterGreenButton>
          </div>
          <div
            style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}
          >
            <SecondaryButton onClick={openSharePopup}>
              Compartilhar
            </SecondaryButton>
            <PrimaryButton onClick={closePopup}>Concluir</PrimaryButton>
          </div>
        </div>

        {/* PDFs */}
        {currentGuides.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '8px',
              marginBottom: '16px',
            }}
          >
            {currentGuides.map(({ file, guide }, jjj) => (
              <a
                key={jjj}
                onClick={() =>
                  downloadPDF(getPdfDownloadUrl(file.product_code, guide))
                }
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: '1px solid #e8e8e8',
                  backgroundColor: '#fff',
                  cursor: 'pointer',
                  fontSize: '12px',
                  color: PrimaryTitleGray,
                  textDecoration: 'none',
                  transition: 'border-color 0.15s',
                }}
              >
                <BsFilePdf
                  style={{ fontSize: '18px', color: '#e53935', flexShrink: 0 }}
                />
                <span
                  style={{
                    maxWidth: '160px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getLastFromSplit(guide, '_')}
                </span>
              </a>
            ))}
          </div>
        )}

        {/* Selecionadas */}
        {currentSelectedRows.length > 0 && (
          <div style={{ marginBottom: '16px' }}>
            <h3
              style={{
                fontSize: '13px',
                fontWeight: '600',
                color: PrimaryTitleGray,
                margin: '0 0 8px',
              }}
            >
              Selecionadas
            </h3>
            <div
              style={{
                borderBottom: '2px dashed #128d15',
                marginBottom: '12px',
                maxHeight: '220px',
                overflow: 'auto',
              }}
            >
              <div style={{ minWidth: isTabletOrMobile ? '520px' : '100%' }}>
                <OrderFlowTable
                  ariaLabel="Soluções selecionadas"
                  header={['Problema', 'Causa', 'Solução']}
                  data={currentSelectedRows.map((x) => {
                    const c = { ...x };
                    delete c.genId;
                    return c;
                  })}
                />
              </div>
            </div>
          </div>
        )}

        {/* Resultado da pesquisa ou estado vazio */}
        <div style={{ overflow: 'auto', flex: 1 }}>
          {searchLoading ? (
            <div style={{ textAlign: 'center', padding: '20px' }}>
              <Spinner />
            </div>
          ) : hasSearchResults ? (
            tableResult()
          ) : (
            <EmptyState message="Nenhuma solução encontrada para este equipamento. Utilize a busca acima ou conclua o atendimento." />
          )}
        </div>
      </div>
    );
  };

  const allActiveSelected =
    equipmentList.filter((eq) => getEquipmentQuantity(eq) > 0).length > 0 &&
    equipmentList
      .filter((eq) => getEquipmentQuantity(eq) > 0)
      .every((eq) => selectedEquipments[eq.id] === true);

  return (
    <>
      {toastMessage && (
        <Toast message={toastMessage} onClose={() => setToastMessage('')} />
      )}

      <LargeTile margin="15px">
        <OrderFlowPageHeader
          title="Assistência técnica"
          subtitle={`Solicite assistência para os equipamentos de ${
            selectedPatient?.name ?? 'o paciente'
          }.`}
        />
        <Panel style={{ padding: '20px' }}>
          <div
            className="titulo"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              marginBottom: '16px',
            }}
          >
            <div style={{ display: 'flex', gap: '10px' }}>
              <Tooltip title="Validação e Processamento de Pedidos">
                <SecondaryButton
                  onClick={proceedWithGenericRequest}
                  style={{ boxSizing: 'border-box', height: '30px' }}
                  disabled={isLoading}
                >
                  <ArrowForward />
                </SecondaryButton>
              </Tooltip>
              <PrimaryButton
                disabled={
                  equipmentList.length === 0 ||
                  Object.keys(selectedEquipments).length === 0
                }
                onClick={proceed}
              >
                Prosseguir
              </PrimaryButton>
            </div>
          </div>

          <div style={{ overflow: 'auto' }}>
            {isLoading ? (
              <div
                style={{
                  width: '100%',
                  textAlign: 'center',
                  padding: '40px 0',
                }}
              >
                <Spinner />
              </div>
            ) : equipmentList.length > 0 ? (
              <OrderFlowTable
                ariaLabel="Equipamentos disponíveis para assistência"
                header={[
                  <div
                    key="select-all-header"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '2px',
                    }}
                  >
                    <input
                      key="select-all"
                      type="checkbox"
                      checked={allActiveSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      title="Selecionar todos"
                    />
                    <span
                      style={{
                        fontSize: '10px',
                        fontWeight: 600,
                        lineHeight: '11px',
                        textAlign: 'center',
                      }}
                    >
                      Selecionar
                      <br />
                      Todos
                    </span>
                  </div>,
                  'Equipamentos e Acessórios',
                  'Categoria',
                  'Subcategoria',
                  'Nº Serie',
                  'Quantidade',
                ]}
                data={equipmentList.map((equipment) => {
                  const quantity = getEquipmentQuantity(equipment);
                  const isDisabled = quantity <= 0;
                  const disabledOpacity = isDisabled ? 0.45 : 1;
                  return {
                    a: (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          opacity: disabledOpacity,
                        }}
                      >
                        <input
                          type="checkbox"
                          onChange={(e) =>
                            selectEquipment(e.target.value, e.target.checked)
                          }
                          value={equipment.id}
                          checked={
                            isDisabled
                              ? false
                              : selectedEquipments[equipment.id] ?? false
                          }
                          disabled={isDisabled}
                          style={{
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                          }}
                        />
                      </div>
                    ),
                    name: (
                      <span style={{ opacity: disabledOpacity }}>
                        {equipment.name}
                      </span>
                    ),
                    category: (
                      <span style={{ opacity: disabledOpacity }}>
                        {equipment.category}
                      </span>
                    ),
                    subCategory: (
                      <span style={{ opacity: disabledOpacity }}>
                        {equipment.subCategory}
                      </span>
                    ),
                    serialNumber: (
                      <span style={{ opacity: disabledOpacity }}>
                        {equipment.serialNumber == 0
                          ? '-'
                          : equipment.serialNumber}
                      </span>
                    ),
                    qty: (
                      <span style={{ opacity: disabledOpacity }}>
                        {quantity}
                      </span>
                    ),
                    showExpandPanelIcon: false,
                  };
                })}
              />
            ) : (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '40px 20px',
                  gap: '10px',
                  color: PrimaryTitleGray,
                  textAlign: 'center',
                }}
              >
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <p
                  style={{
                    margin: 0,
                    fontSize: '13px',
                    lineHeight: '1.6',
                    maxWidth: '420px',
                  }}
                >
                  Detectamos uma incompatibilidade nas informações do cadastro.
                  Por favor, siga com o pedido clicando no botão acima e
                  descreva sua solicitação.
                </p>
              </div>
            )}
          </div>
        </Panel>
      </LargeTile>

      {showPopup && (
        <SimplePopup height={`${popupHeight}px`} outlet={showHelp()} />
      )}
      {showCreateLinkPopup && (
        <SimplePopup
          className="pop-up-assistance"
          height={`${popupHeight}px`}
          width={800}
          outlet={createLinkPopup()}
        />
      )}
    </>
  );
};

export const TechnicalAssistancePage = () => {
  return <WideLayout outlet={MainPanels()} />;
};
